import { useState, useEffect, useRef } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Round, Player, GameState, QuestionType } from '@brainfart/shared';
import { ScoreChip } from './ScoreChip';
import { CircularTimer } from './CircularTimer';
import { AnswerOption } from './AnswerOption';
import { ScreenHeader } from './ScreenHeader';
import { LeaveGame } from './LeaveGame';
import { PromptText } from './PromptText';

interface QuestionScreenProps {
  round: Round;
  game: GameState;
  me: Player;
  onSubmit: (roundId: string, answer: string) => void;
  onForceNext: (roundId: string) => void;
  isHost: boolean;
  onLeave: () => void;
}

export function QuestionScreen({ round, game, me, onSubmit, onForceNext, isHost, onLeave }: QuestionScreenProps) {
  const { question } = round;
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const submittedRef = useRef(false);

  const answeredCount = game.players.filter(p => p.hasAnswered).length;
  const totalCount = game.players.filter(p => p.connected).length;
  const locked = submitted || timedOut;

  // Auto-focus input
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSubmit = (explicitAnswer?: string) => {
    const finalAnswer = (explicitAnswer ?? answer).trim();
    if (submittedRef.current || timedOut || !finalAnswer) return;
    submittedRef.current = true;
    setSubmitted(true);
    if (explicitAnswer !== undefined) setAnswer(explicitAnswer);
    onSubmit(round.id, finalAnswer);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Question type specific rendering
  const renderInput = () => {
    switch (question.type) {
      case 'numeric':
        return (
          <input
            ref={inputRef}
            type="number"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a number..."
            style={{
              width: '100%',
              padding: '1.25rem',
              fontSize: '2.5rem',
              fontWeight: 700,
              textAlign: 'center',
              borderRadius: '16px',
              border: `2px solid ${focused ? question.category === 'Science' ? '#00d4ff' : '#FFC93C' : '#2E2B45'}`,
              background: '#100E22',
              color: '#fff',
              outline: 'none',
              fontFamily: 'monospace',
            }}
            disabled={locked}
            autoComplete="off"
          />
        );

      case 'multiple-choice':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {question.options?.map((option, idx) => (
              <AnswerOption
                key={option}
                letter={String.fromCharCode(65 + idx)}
                label={option}
                state={submitted && answer === option ? 'selected' : 'idle'}
                disabled={locked}
                onClick={() => handleSubmit(option)}
                delay={idx * 0.1}
              />
            ))}
          </div>
        );

      case 'order':
        return (
          <OrderInput
            items={question.items || []}
            answer={answer}
            onChange={setAnswer}
            onSubmit={() => handleSubmit()}
            submitted={submitted}
          />
        );

      default:
        return (
          <input
            ref={inputRef}
            type="text"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="Type your answer..."
            style={{
              width: '100%',
              padding: '1.25rem',
              fontSize: '1.25rem',
              fontWeight: 500,
              textAlign: 'center',
              borderRadius: '16px',
              border: `2px solid ${focused ? '#FFC93C' : '#2E2B45'}`,
              background: '#100E22',
              color: '#fff',
              outline: 'none',
            }}
            disabled={locked}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        );
    }
  };

  return (
    <m.div
      className="question-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0F0D1F' }}
    >
      <ScreenHeader
        left={
          <span style={{ color: '#888', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
            {question.category}
          </span>
        }
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <CircularTimer
              key={round.id}
              limitSeconds={game.settings.roundTimeLimit}
              onExpire={() => setTimedOut(true)}
            />
            <div style={{ color: '#666', fontSize: '0.8rem' }}>
              {game.currentRound + 1}/{game.settings.rounds}
            </div>
            <ScoreChip players={game.players} meId={me.id} />
          </div>
        }
      />

      {/* Question Content. Extra bottom padding keeps the host "Force next"
          control clear of the fixed emoji ReactionBar during a live round. */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1.25rem 1.25rem 6.5rem', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        {/* Question Prompt */}
        <m.div
          className="question-prompt"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ marginBottom: '1.5rem', textAlign: 'center' }}
        >
          <h1 style={{
            fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
            fontWeight: 700,
            lineHeight: 1.3,
            color: '#fff',
            marginBottom: '1rem',
          }}>
            <PromptText prompt={question.prompt} />
          </h1>
        </m.div>

        {/* Input Area */}
        <m.div
          className="answer-input"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}
        >
          {renderInput()}

          {/* Submit button: only for typed answers. Multiple choice submits on tap. */}
          <AnimatePresence>
            {!submitted && question.type !== 'multiple-choice' && (
              <m.button
                onClick={() => handleSubmit()}
                disabled={!answer.trim() || locked}
                className="btn btn-primary"
                style={{ width: '100%', marginTop: '1.5rem', padding: '1.125rem', fontSize: '1.1rem' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Submit answer
              </m.button>
            )}
          </AnimatePresence>
        </m.div>

        {/* Answered Count — doubles as the "you're locked in" confirmation */}
        <m.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ textAlign: 'center', marginTop: '1.5rem', color: submitted ? '#39FF88' : '#999', fontSize: '0.9rem', fontWeight: 600 }}
        >
          {submitted ? '✅ ' : ''}{answeredCount} / {totalCount} answered
        </m.p>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
          {isHost && (
            <button onClick={() => onForceNext(round.id)} className="text-link">
              ⏭ Someone stuck? Force next
            </button>
          )}
          <LeaveGame onLeave={onLeave} />
        </div>
      </main>
    </m.div>
  );
}

// Order Input Component
function OrderInput({ items, answer, onChange, onSubmit, submitted }: {
  items: string[];
  answer: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
  submitted: boolean;
}) {
  const [ordered, setOrdered] = useState<string[]>(() => {
    try { return answer ? JSON.parse(answer) : [...items]; } catch { return [...items]; }
  });

  const moveItem = (fromIdx: number, toIdx: number) => {
    const newOrdered = [...ordered];
    const [item] = newOrdered.splice(fromIdx, 1);
    newOrdered.splice(toIdx, 0, item);
    setOrdered(newOrdered);
    onChange(JSON.stringify(newOrdered));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {ordered.map((item, idx) => (
        <m.div
          key={item}
          className="order-item"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '1rem 1.25rem',
            background: '#1B1830',
            border: '2px solid #2E2B45',
            borderRadius: '12px',
            cursor: submitted ? 'not-allowed' : 'grab',
          }}
        >
          <span style={{
            width: '40px',
            textAlign: 'center',
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#888',
          }}>{idx + 1}</span>

          <span style={{ flex: 1, fontSize: '1.1rem' }}>{item}</span>

          {!submitted && (
            <>
              <button
                onClick={() => idx > 0 && moveItem(idx, idx - 1)}
                disabled={idx === 0}
                style={{
                  padding: '0.5rem',
                  background: '#211E3B',
                  border: '1px solid #2E2B45',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: idx === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                ▲
              </button>
              <button
                onClick={() => idx < ordered.length - 1 && moveItem(idx, idx + 1)}
                disabled={idx === ordered.length - 1}
                style={{
                  padding: '0.5rem',
                  background: '#211E3B',
                  border: '1px solid #2E2B45',
                  borderRadius: '8px',
                  color: '#888',
                  cursor: idx === ordered.length - 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ▼
              </button>
            </>
          )}
        </m.div>
      ))}

      {!submitted && (
        <button onClick={onSubmit} className="btn btn-primary" style={{ marginTop: '1rem', width: '100%' }}>
          Submit Order
        </button>
      )}
    </div>
  );
}

